import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { BRAND } from "@/lib/brand";
import { Users, HeartHandshake, Leaf, Briefcase, Sparkles, CalendarRange } from "lucide-react";

const itemConfig = [
    { key: "families", icon: Users, image: BRAND.images.family, size: "tall" },
    { key: "couples", icon: HeartHandshake, image: BRAND.images.couple },
    { key: "healthy", icon: Leaf, image: BRAND.images.healthy },
    { key: "corporate", icon: Briefcase, image: BRAND.images.corporate },
    { key: "events", icon: Sparkles, image: BRAND.images.event, size: "wide" },
    { key: "mealprep", icon: CalendarRange, image: BRAND.images.mealprep },
];

const Card = ({ item, idx, t }) => {
    const Icon = item.icon;
    const title = t(`who_for.items.${item.key}.title`);
    const desc = t(`who_for.items.${item.key}.desc`);
    const isImage = !!item.image;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: idx * 0.06, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`who-card-${idx}`}
            className={`group relative overflow-hidden rounded-2xl ring-1 ring-stone-200 ${
                item.size === "tall" ? "md:row-span-2" : ""
            } ${item.size === "wide" ? "md:col-span-2" : ""}`}
        >
            {isImage ? (
                <>
                    <img
                        src={item.image}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/85 via-stone-900/30 to-transparent" />
                    <div className="relative h-full min-h-[280px] md:min-h-[300px] p-7 flex flex-col justify-end text-stone-50">
                        <Icon className="text-gold-soft mb-3" size={22} strokeWidth={1.5} />
                        <h3 className="font-serif text-2xl md:text-3xl leading-tight">{title}</h3>
                        <p className="mt-2 text-stone-200/90 text-sm md:text-base font-light">{desc}</p>
                    </div>
                </>
            ) : (
                <div className="relative h-full min-h-[260px] md:min-h-[300px] p-7 flex flex-col justify-between bg-beige-50">
                    <Icon className="text-gold-deep" size={26} strokeWidth={1.4} />
                    <div>
                        <h3 className="font-serif text-2xl md:text-3xl text-stone-900 leading-tight">{title}</h3>
                        <p className="mt-2 text-stone-600 text-sm md:text-base font-light">{desc}</p>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export const WhoFor = () => {
    const { t } = useTranslation();
    return (
        <section id="para-quem" data-testid="who-for-section" className="py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-2xl mb-14 md:mb-20">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="gold-divider" />
                        <span className="text-xs uppercase tracking-[0.24em] text-stone-500">
                            {t("who_for.eyebrow")}
                        </span>
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl text-stone-900 tracking-tight leading-[1.05]">
                        {t("who_for.title_1")}{" "}
                        <em className="italic text-gold-deep font-normal">{t("who_for.title_em")}</em>
                        .
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 auto-rows-[1fr]">
                    {itemConfig.map((it, i) => (
                        <Card key={it.key} item={it} idx={i} t={t} />
                    ))}
                </div>
            </div>
        </section>
    );
};
